import {
  Column,
  Container,
  ElasticContentContainer,
  EmptyState,
  PageHeader,
  ReportsPageTabs,
  Row,
  ScrollableWrapper
} from '@cdab/scania/qpr/components'
import { getClient } from '@cdab/scania/qpr/contexts/backend-provider'
import { useTitle } from '@cdab/scania/qpr/contexts/title'
import { useCssVariableBreakpoint } from '@cdab/scania/qpr/hooks'
import { getReportTypeFromParams } from '@cdab/scania/qpr/loaders'
import type { DOSVersion } from '@cdab/scania/qpr/schema'
import { type Area, type Market } from '@cdab/scania/qpr/schema'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { LoaderFunctionArgs } from 'react-router'
import { Outlet, isRouteErrorResponse, useRouteError } from 'react-router'
import { json } from 'react-router-dom'
import styled from 'styled-components'

export type ReportsPageLoaderData = {
  markets: Market[]
  userMarkets: Market[]
  areas: Area[]
  dosVersions: DOSVersion[]
}

const StyledRow = styled(Row)`
  display: flex;
  flex-direction: column;
  padding-left: 16px;
  padding-right: 16px;
`
const marketReportTypes = [
  'area-summary',
  'country-summary',
  'top-market-deviations',
  'statistics'
]

export type ReportLoaderData = {
  reportType: string
}

export async function reportLoader({
  params
}: LoaderFunctionArgs): Promise<ReportLoaderData> {
  const reportType = getReportTypeFromParams(params)

  if (!marketReportTypes.includes(reportType))
    throw new Response(reportType, {
      status: 404
    })

  return { reportType }
}

export async function loader(): Promise<ReportsPageLoaderData> {
  const client = getClient()

  const userInfo = await client.GetuserInfo()
  if (!userInfo) {
    throw json(null, {
      status: 401
    })
  }

  try {
    const [markets, userMarkets, areas, dosVersions] = await Promise.all([
      client.MarketsService.GetAllMarkets(),
      client.MarketsService.GetMarketsForUser(userInfo.userId),
      client.MarketsService.GetAllAreas(),
      client.UserService.GetDOSVersionsForUser()
    ])

    return {
      markets,
      userMarkets,
      areas,
      dosVersions
    }
  } catch (error) {
    throw new Response(null, { status: 404 })
  }
}

export function ReportsPage() {
  const { t } = useTranslation(['reports', 'common'])
  const { updateTitles } = useTitle()
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')

  useEffect(() => {
    updateTitles({
      mobile: {
        title: t('reports'),
        description: null
      }
    })
  }, [updateTitles, t])

  return (
    <ElasticContentContainer
      overflowHidden={true}
      header={
        <>
          {isLg && (
            <Row>
              <Column width={12}>
                <PageHeader title={t('reports')} />
              </Column>
            </Row>
          )}
          <StyledRow>
            <ReportsPageTabs
              links={{
                areaSummary: 'area-summary',
                countrySummary: 'country-summary',
                topMarketDeviations: 'top-market-deviations',
                statistics: 'statistics'
              }}
            />
          </StyledRow>
        </>
      }
    >
      <StyledRow fullHeight>
        <ScrollableWrapper>
          <Outlet />
        </ScrollableWrapper>
      </StyledRow>
    </ElasticContentContainer>
  )
}

export function ErrorBoundary() {
  const { t } = useTranslation('errors')
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    let message = t('talk-to-admin')

    switch (error.status) {
      case 401:
        message = t('not-logged-in')
        break
      case 403:
        message = t('access-denied')
        break
    }
    return (
      <Container
        fluid='normal'
        paddingOnColumns={!isLg}
        paddingOnContainer={!isLg}
      >
        <Row>
          <Column width={12} padding={isLg}>
            <EmptyState
              title={t('could-not-fetch-reports')}
              description={message}
            />
          </Column>
        </Row>
      </Container>
    )
  }

  // rethrow to let the parent error boundary handle it
  // when it's not a special case for this route
  throw error
}
